import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Database, Brain, Sparkles, ChevronDown } from "lucide-react";
import Layout from "@/components/Layout";
import Section from "@/components/Section";
import OfferCard from "@/components/OfferCard";
import HeroDiagram from "@/components/HeroDiagram";
import TrustTicker from "@/components/TrustTicker";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { useTranslation } from "@/i18n/useTranslation";
import { usePageSeo } from "@/seo/usePageSeo";
import { faqPageJsonLd } from "@/seo/jsonLd";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { t, locale } = useTranslation();
  const founderTags = locale === "fr"
    ? ["Systèmes IA", "Ingénierie de données", "ML / DS", "Systèmes décisionnels", "Mesure de rentabilité"]
    : ["AI systems", "Data engineering", "ML / DS", "Decision systems", "Profitability measurement"];

  const domainIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const domains = domainIndices.map((i) => ({
    label: t(`home.domain${i}Label`),
    desc: t(`home.domain${i}Desc`),
  }));

  const faqs = Array.from({ length: 6 }, (_, i) => ({
    q: t(`home.faq${i + 1}Q`),
    a: t(`home.faq${i + 1}A`),
  }));

  const seo = usePageSeo("home");
  const faqJsonLd = faqPageJsonLd(faqs);

  return (
    <Layout>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={faqJsonLd as unknown as Record<string, unknown>}
      />
      {/* Hero — dark, full viewport, split layout */}
      <section className="relative min-h-[80vh] md:min-h-[92vh] section-dark grain overflow-hidden flex items-center">
        {/* Mobile: diagram as subtle background */}
        <div className="lg:hidden">
          <HeroDiagram className="opacity-50" />
        </div>

        {/* Subtle dot grid texture behind text column */}
        <div className="absolute inset-0 dot-grid opacity-[0.25] pointer-events-none" />

        <div className="container-wide relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: text */}
            <div className="max-w-2xl">
              <p className="label-sm mb-7 !text-primary/55 opacity-0 animate-fade-up stagger-1">{t("home.heroLabel")}</p>
              <div className="w-10 h-px bg-primary/25 mb-10 opacity-0 animate-fade-up stagger-1 animate-breathe" />
              <h1 className="heading-hero text-[hsl(var(--sd-fg))] mb-10 opacity-0 animate-fade-up stagger-2">
                {t("home.heroTitle")}
              </h1>
              <p className="text-lead text-[hsl(var(--sd-fg)/0.40)] max-w-md mb-14 opacity-0 animate-fade-up stagger-3 font-light whitespace-pre-line">
                {t("home.heroSubtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 opacity-0 animate-fade-up stagger-4">
                <a href="https://calendly.com/d/cvpp-rwv-v5j" target="_blank" rel="noopener noreferrer" className="btn-primary group">
                  {t("nav.talkToUs")}
                  <ArrowRight className="w-3 h-3" />
                </a>
                <Link
                  to="/services"
                  className="btn-outline"
                >
                  {t("nav.seeWhatWeBuild")}
                </Link>
              </div>
            </div>

            {/* Right: diagram (desktop only) */}
            <div className="relative hidden lg:flex items-center justify-end min-h-[480px]">
              <HeroDiagram className="opacity-90 justify-end" />
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 pb-24">
          <ChevronDown className="w-4 h-4 text-[hsl(var(--sd-fg)/0.15)] animate-scroll-hint" />
        </div>
      </section>

      <TrustTicker />

      {/* Problem-recognition bridge */}
      <div className="container-wide py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start max-w-3xl">
          <div className="hidden md:flex flex-col items-center gap-2 pt-1.5">
            <div className="w-px h-16 bg-primary/25 animate-breathe" />
            <div className="w-1.5 h-1.5 rounded-full bg-primary/30" />
          </div>
          <div>
            <p className="font-mono text-[10.5px] font-medium tracking-[0.12em] uppercase text-primary/40 mb-5">
              {locale === "fr" ? "Le vrai défi" : "The real challenge"}
            </p>
            <p className="text-[17px] md:text-[19px] text-foreground/75 font-light leading-[1.75] tracking-[-0.01em]">
              {locale === "fr"
                ? "La plupart des entreprises ont une stratégie IA. Elles manquent de systèmes qui fonctionnent en production, qui se maintiennent, et qui génèrent un retour mesurable."
                : "Many companies have an AI strategy. Most of them lack systems that run in production, hold up over time, and deliver measurable returns."}
            </p>
            <p className="text-[15px] text-primary/90 font-medium mt-4 tracking-[-0.01em]">
              {locale === "fr" ? "C'est exactement ce qu'on construit." : "That is exactly what we build."}
            </p>
          </div>
        </div>
      </div>

      {/* Differentiators — 2x2 grid */}
      <Section fullBleed animate>
        <div className="container-wide section-padding">
          <div className="mb-20">
            <p className="label-rule mb-6">{t("home.whyUsLabel")}</p>
            <h2 className="heading-section-lg text-foreground leading-tight mb-6 whitespace-pre-line max-w-lg">
              {t("home.whyUsTitle")}
            </h2>
            <p className="text-muted-foreground text-[15px] leading-[1.8] max-w-md font-light">
              {t("home.whyUsSubtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/8">
            {[
              { title: t("home.diff1Title"), desc: t("home.diff1Desc") },
              { title: t("home.diff2Title"), desc: t("home.diff2Desc") },
              { title: t("home.diff3Title"), desc: t("home.diff3Desc") },
              { title: t("home.diff4Title"), desc: t("home.diff4Desc") },
            ].map((item, i) => (
              <div
                key={item.title}
                className={`p-8 md:p-12 group transition-all duration-600 bg-background hover:bg-surface-1 relative overflow-hidden`}
              >
                {/* Subtle corner accent on hover */}
                <div className="absolute top-0 left-0 w-12 h-px bg-primary/0 group-hover:bg-primary/20 transition-all duration-700" />
                <div className="absolute top-0 left-0 w-px h-12 bg-primary/0 group-hover:bg-primary/20 transition-all duration-700" />
                
                <span className="font-mono text-[3rem] md:text-[4rem] font-extralight leading-none text-foreground/[0.02] group-hover:text-primary/8 transition-colors duration-600 block mb-7">
                  0{i + 1}
                </span>
                <h3 className="text-[15px] font-semibold text-foreground mb-3.5 font-sans tracking-[-0.01em]">{item.title}</h3>
                <p className="text-[15px] text-muted-foreground leading-[1.8] font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Core offers */}
      <Section>
        <p className="label-sm mb-6">{t("home.engagementsLabel")}</p>
        <h2 className="heading-section text-foreground mb-4 max-w-lg leading-tight whitespace-pre-line">
          {t("home.engagementsTitle")}
        </h2>
        <p className="text-lead text-muted-foreground max-w-lg mb-16 font-light">
          {t("home.engagementsSubtitle")}
        </p>
        <div>
          <OfferCard num="01" icon={Cpu} title={t("home.offer1Title")} description={t("home.offer1Desc")} duration={locale === "fr" ? "4–14 sem." : "4–14 weeks"} href="/services/agentic-ai" />
          <OfferCard num="02" icon={Sparkles} title={t("home.offer2Title")} description={t("home.offer2Desc")} duration={locale === "fr" ? "4–12 sem." : "4–12 weeks"} href="/services/generative-ai" />
          <OfferCard num="03" icon={Brain} title={t("home.offer3Title")} description={t("home.offer3Desc")} duration={locale === "fr" ? "4–14 sem." : "4–14 weeks"} href="/services/predictive-ai" />
          <OfferCard num="04" icon={Database} title={t("home.offer4Title")} description={t("home.offer4Desc")} duration={locale === "fr" ? "4–14 sem." : "4–14 weeks"} href="/services/data-platforms" />
          <div className="separator-dot mt-2">
            <div className="separator-dot-center" />
          </div>
        </div>
      </Section>

      {/* Implementation domains — 3-column grid, clickable */}
      <Section padding="sm">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-16">
          <div>
            <p className="label-sm mb-6">{t("home.domainsLabel")}</p>
            <h2 className="heading-section text-foreground mb-5 leading-tight max-w-md whitespace-pre-line">
              {t("home.domainsTitle")}
            </h2>
            <p className="text-[15px] text-muted-foreground max-w-xl leading-[1.8] font-light">
              {t("home.domainsSubtitle")}
            </p>
          </div>
          <Link
            to="/solutions"
            className="link-underline font-mono text-[10.5px] font-medium tracking-[0.1em] uppercase text-foreground flex items-center gap-1.5 flex-shrink-0 transition-colors duration-400"
          >
            {locale === "fr" ? "Toutes les solutions" : "All solutions"} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-0">
          {domains.map((item, i) => {
            const hrefs: (string | null)[] = [
              "/solutions/ai-agents-workflow-automation",
              "/solutions/customer-support-service-systems",
              "/solutions/knowledge-systems-enterprise-retrieval",
              "/solutions/document-intelligence",
              "/solutions/predictive-models-decision-science",
              null, // Recommendation — no dedicated page
              null, // Profitability — no dedicated page
              "/solutions/data-platforms-for-ai",
              null, // AI operations — no dedicated page
            ];
            const href = hrefs[i];
            const content = (
              <>
                <h3 className="text-[14px] font-semibold text-foreground font-sans flex items-center gap-2.5 mb-3 tracking-[-0.01em] group-hover:text-primary transition-colors duration-400">
                  <span className="w-1 h-1 rounded-full flex-shrink-0 transition-colors duration-500 bg-primary/25 group-hover:bg-primary/50" />
                  {item.label}
                  {href && <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-40 group-hover:translate-x-0 transition-all duration-400 ml-auto flex-shrink-0" />}
                </h3>
                <p className="text-[13.5px] text-muted-foreground/70 leading-[1.75] font-light pl-[18px]">{item.desc}</p>
              </>
            );
            return href ? (
              <Link
                key={item.label}
                to={href}
                className="border-t border-border/10 py-9 group transition-all duration-400 hover:bg-primary/[0.02] block px-1 -mx-1 rounded-sm"
              >
                {content}
              </Link>
            ) : (
              <div
                key={item.label}
                className="border-t border-border/10 py-9 group block px-1 -mx-1 rounded-sm"
              >
                {content}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Authority guides — compact inline section */}
      <Section padding="sm" className="ambient-glow">
        <div className="border border-border/10 rounded-sm p-8 md:p-12 bg-surface-1">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <p className="label-sm mb-4">{locale === "fr" ? "Guides stratégiques" : "Strategic guides"}</p>
              <h2 className="heading-subsection text-foreground leading-tight">
                {locale === "fr" ? "Références pour les décideurs." : "References for decision-makers."}
              </h2>
            </div>
            <Link to="/insights" className="link-underline font-mono text-[10.5px] font-medium tracking-[0.1em] uppercase text-foreground flex items-center gap-1.5 flex-shrink-0 transition-colors duration-400">
              {t("home.allInsights")} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: locale === "fr" ? "Comment évaluer un fournisseur IA" : "How to Vet an AI Vendor", desc: locale === "fr" ? "Distinguer la vraie capacité du marketing." : "Separate real capability from polished marketing.", href: "/insights/vet-ai-vendor" },
              { title: locale === "fr" ? "Ce que signifie l'IA de production" : "What 'Production-Grade AI' Means", desc: locale === "fr" ? "Ce qui sépare une démo d'un système fiable." : "What separates a demo from a reliable system.", href: "/insights/production-grade-ai" },
              { title: locale === "fr" ? "Quels processus méritent l'IA" : "Which Workflows Deserve AI", desc: locale === "fr" ? "Où l'IA crée de la valeur et où elle n'en crée pas." : "Where AI creates value and where it doesn't.", href: "/insights/workflows-deserve-ai" },
            ].map((guide) => (
              <Link key={guide.href} to={guide.href} className="group block py-4 px-1 -mx-1 rounded-sm hover:bg-primary/[0.02] transition-colors duration-400">
                <div className="flex items-start gap-3">
                  <span className="w-1 h-1 rounded-full bg-primary/20 flex-shrink-0 mt-2 group-hover:bg-primary/40 transition-colors duration-400" />
                  <div>
                    <span className="text-[14px] text-foreground font-medium group-hover:text-primary transition-colors duration-400 leading-snug block mb-1.5">
                      {guide.title}
                    </span>
                    <span className="text-[13px] text-muted-foreground/60 font-light leading-[1.6]">
                      {guide.desc}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* Founder — dark cinematic with monogram */}
      <Section fullBleed dark grain animate>
        <div className="container-wide section-padding relative z-10">
          <div className="absolute top-1/2 right-12 -translate-y-1/2 font-mono text-[12rem] md:text-[18rem] font-bold text-[hsl(var(--sd-fg)/0.012)] leading-none pointer-events-none select-none hidden lg:block">
            GB
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 lg:gap-24 items-start relative">
            <div>
              <div className="w-px h-20 bg-primary/20 mb-6" />
              <p className="text-[15px] font-medium text-[hsl(var(--sd-fg)/0.50)]">{t("home.founderName")}</p>
              <p className="font-mono text-[10.5px] text-[hsl(var(--sd-fg)/0.22)] mt-1.5 tracking-[0.12em] uppercase">{t("home.founderRole")}</p>
            </div>
            <div>
              <p className="label-sm !text-primary/55 mb-6">{t("home.founderLabel")}</p>
              <h2 className="heading-section text-[hsl(var(--sd-fg)/0.70)] mb-10 leading-tight">
                {t("home.founderName")}
              </h2>
              <div className="space-y-6 text-[15px] text-[hsl(var(--sd-fg)/0.35)] leading-[1.8] max-w-lg font-light">
                <p>{t("home.founderP1")}</p>
                <p>{t("home.founderP2")}</p>
                <p className="text-[hsl(var(--sd-fg)/0.55)] font-medium">{t("home.founderP3")}</p>
              </div>
              <div className="mt-14 flex flex-wrap gap-2.5">
                {founderTags.map((tag) => (
                  <span key={tag} className="inline-block rounded-sm bg-[hsl(var(--sd-fg)/0.02)] border border-[hsl(var(--sd-fg)/0.05)] px-4 py-2 font-mono text-[10px] font-medium text-[hsl(var(--sd-fg)/0.22)] tracking-[0.12em] uppercase hover:border-primary/20 hover:text-[hsl(var(--sd-fg)/0.38)] transition-all duration-400">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>
      {/* FAQ */}
      <Section narrow>
        <p className="label-sm mb-6">{t("home.faqLabel")}</p>
        <h2 className="heading-section text-foreground mb-16 leading-tight">
          {t("home.faqTitle")}
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border/10">
              <AccordionTrigger className="text-left text-foreground font-sans font-medium text-[15px] py-7 px-4 hover:no-underline hover:text-primary transition-colors duration-500">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-[1.8] text-[15px] pb-7 px-4 font-light">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <CTABanner variant="dark" headline={t("home.ctaHeadline")} description={t("home.ctaDescription")} />
    </Layout>
  );
};

export default Index;
